import * as R from "ramda";
import uuid from "uuid/v1";
import { api } from "../../api/api";
import * as moment from "moment";
import * as Promise from "bluebird";
Promise.config({ warnings: false, longStackTraces: false, asyncHooks: false });

const toCamelCase = (str, delimiters) => {
  var DEFAULT_REGEX = /[-_]+(.)?/g;

  const toUpper = (match, group1) => {
    return group1 ? group1.toUpperCase() : "";
  };

  return str.replace(
    delimiters ? new RegExp("[" + delimiters + "]+(.)?", "g") : DEFAULT_REGEX,
    toUpper
  );
};

const modelFieldCheckType = (modelFields) => (field) => (type) =>
  modelFields[field].type === type;

const modelFieldDefaultValue = (modelFields) => (field) =>
  modelFields[field].default || "";

const modelFieldDefaultValueBool = (modelFields) => (field) =>
  modelFields[field].default || false;

const getModelKeys = (modelFields) => R.keys(modelFields);

const getBool = (val) => {
  var num = +val;
  return val
    ? !isNaN(num)
      ? !!num
      : !!String(val).toLowerCase().replace(!!0, "")
    : false;
};

const proccessModelResultFields =
  (model, cache = {}) =>
  (object) =>
    new Promise((success, reject) => {
      if (!model) {
        reject("Model must not be undefined");
      } else {
        const result = R.pipe(
          R.pick(getModelKeys(model.fields)),
          R.mapObjIndexed((value, field) => {
            if (field === "updated_at" || field === "created_at") {
              return typeof value === "string"
                ? moment(value).format("x") * 1
                : value;
            }

            if (
              modelFieldCheckType(model.fields)(field)("array") ||
              modelFieldCheckType(model.fields)(field)("object")
            ) {
              try {
                return JSON.parse(object[field]);
              } catch (e) {
                console.error(e.message);
                return;
              }
            } else if (
              modelFieldCheckType(model.fields)(field)("raw-picture")
            ) {
              if (!cache[result[model.id]]) {
                cache[result[model.id]] = {};
              }
              cache[result[model.id]][field] = value;
              return "RAWPICTURE";
            } else if (modelFieldCheckType(model.fields)(field)("boolean")) {
              return getBool(
                R.defaultTo(modelFieldDefaultValueBool(model.fields)(field))(
                  value
                )
              );
            }
            return R.defaultTo(modelFieldDefaultValue(model.fields)(field))(
              value
            );
          })
        )(object);

        success(result);
      }
    });

export const datasource =
  (currentUserId, actions = {}) =>
  (tableName, schema) =>
    new Promise((datasourceSuccess) => {
      const endPointName = toCamelCase(tableName);

      const datasourceActions = R.merge({
        create: () => console.info(`${tableName}: Creating datasource.`),
        created: () => console.info(`${tableName}: Datasource created.`),
        requestStart: () =>
          console.info(`${tableName}: Datasource request start.`),
        requestEnd: () => console.info(`${tableName}: Datasource request end.`),
        requestError: () =>
          console.error(`${tableName}: Datasource request end.`),
      })(actions);

      datasourceActions.create();

      const isNoCalculated = (val) => !val.calculated;
      const schemaFields = R.pickBy(isNoCalculated)(schema.model.fields);
      const idKey = schema.model.id || "id";

      const dSource = {
        tableName,
        schema: {
          ...schema,
          model: {
            ...schema.model,
            id: idKey,
          },
        },
        fields: schemaFields,
      };

      dSource.create = (item) =>
        new Promise((success, reject) => {
          const model = R.pipe(
            (item) =>
              R.assocPath([idKey], R.pathOr(uuid(), ["localId"])(item))(item),
            R.omit(["localId"]),
            R.pick(R.keys(dSource.fields)),
            R.mapObjIndexed((v, f) => {
              if (typeof v == "string" && v.toLowerCase() == "undefined") {
                return "";
              }

              if (dSource.fields[f].type == "number") {
                return v * 1;
              }

              if (
                dSource.fields[f].type == "array" ||
                dSource.fields[f].type == "object"
              ) {
                return JSON.stringify(v);
              }

              if (dSource.fields[f].type == "boolean") {
                return getBool(v.toString());
              }

              return v || "";
            })
          )(item);

          const { ajaxSession, getUserSessionUserData, checkOffline } = api;

          datasourceActions.requestStart();

          const userData = getUserSessionUserData();

          const fixCreatedUpdatedBy = (resultItem) =>
            R.pipe(
              R.assoc("created_by", userData.id),
              R.assoc("updated_by", userData.id)
            )(resultItem);

          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                ajaxSession(`${endPointName}`, {
                  type: "POST",
                  data: fixCreatedUpdatedBy(model),
                })
                  .then((requestedModel) => {
                    const model = R.pipe(
                      R.pick(R.keys(dSource.fields)),
                      R.mapObjIndexed((v, f) => {
                        if (f === "updated_at" || f === "created_at") {
                          return typeof v === "string"
                            ? moment(v).format("x") * 1
                            : v;
                        }
                        if (
                          typeof v == "string" &&
                          (v.toLowerCase() == "undefined" ||
                            v.toLowerCase() == "null")
                        ) {
                          return "";
                        }

                        if (dSource.fields[f].type == "number") {
                          return v * 1;
                        }

                        if (
                          dSource.fields[f].type == "array" ||
                          dSource.fields[f].type == "object"
                        ) {
                          return JSON.stringify(v);
                        }

                        if (dSource.fields[f].type == "boolean") {
                          return getBool(v.toString());
                        }

                        return v || "";
                      })
                    )(requestedModel);

                    datasourceActions.requestEnd();
                    success(model);
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      dSource.get = (id) =>
        new Promise((success, reject) => {
          const { ajaxSession, checkOffline } = api;

          datasourceActions.requestStart();

          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                ajaxSession(`${endPointName}/${id}`, {
                  type: "GET",
                  params: {},
                  data: {},
                })
                  .then((requestedModel) => {
                    const getModel = R.pipe(
                      R.pick(R.keys(dSource.fields)),
                      R.mapObjIndexed((v, f) => {
                        if (f === "updated_at" || f === "created_at") {
                          return typeof v === "string"
                            ? moment(v).format("x") * 1
                            : v;
                        }
                        if (
                          typeof v == "string" &&
                          (v.toLowerCase() == "undefined" ||
                            v.toLowerCase() == "null")
                        ) {
                          return "";
                        }

                        if (dSource.fields[f].type == "boolean") {
                          return getBool(v.toString());
                        }

                        return v || "";
                      })
                    );

                    const models = R.map(getModel)([requestedModel]);
                    datasourceActions.requestEnd();

                    success(models[0]);
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      dSource.read = ({
        id,
        proshopId = "",
        customerId = "",
        specsheetId = "",
        limit = 100,
        offset = 0,
        filter = "",
        orderBy = "id",
        ...data
      }) =>
        new Promise((success, reject) => {
          const { ajaxSession, checkOffline } = api;

          datasourceActions.requestStart();

          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                ajaxSession(
                  R.isNil(id) ? `${endPointName}/` : `${endPointName}/${id}/`,
                  {
                    type: "GET",
                    params: {
                      ...data,
                      proshopId,
                      customerId,
                      specsheetId,
                      limit,
                      offset,
                      filter,
                      orderBy,
                    },
                  }
                )
                  .then((requestedModels) => {
                    const getModel = R.pipe(
                      R.pick(R.keys(dSource.fields)),
                      R.mapObjIndexed((v, f) => {
                        if (f === "updated_at" || f === "created_at") {
                          return typeof v === "string"
                            ? moment(v).format("x") * 1
                            : v;
                        }
                        if (
                          typeof v == "string" &&
                          (v.toLowerCase() == "undefined" ||
                            v.toLowerCase() == "null")
                        ) {
                          return "";
                        }

                        if (dSource.fields[f].type == "boolean") {
                          return getBool(v.toString());
                        }

                        return v || "";
                      })
                    );

                    datasourceActions.requestEnd();

                    if (Array.isArray(requestedModels)) {
                      const models = R.map(getModel)(requestedModels);
                      success(models);
                    } else {
                      success(requestedModels);
                    }
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      dSource.update = (item) =>
        new Promise((success, reject) => {
          const model = R.pipe(
            R.omit(["localId"]),
            R.pick(R.keys(dSource.fields)),
            R.mapObjIndexed((v, f) => {
              if (typeof v == "string" && v.toLowerCase() == "undefined") {
                return "";
              }

              if (dSource.fields[f].type == "number") {
                return v * 1;
              }

              if (
                dSource.fields[f].type == "array" ||
                dSource.fields[f].type == "object"
              ) {
                return JSON.stringify(v);
              }

              if (dSource.fields[f].type == "boolean") {
                return getBool(v.toString());
              }

              return v || "";
            })
          )(item);

          const { ajaxSession, getUserSessionUserData, checkOffline } = api;

          datasourceActions.requestStart();

          const userData = getUserSessionUserData();

          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                ajaxSession(`${`${endPointName}`}/${model.id}`, {
                  type: "PUT",
                  data: model,
                })
                  .then((requestedModel) => {
                    const model = R.pipe(
                      R.pick(R.keys(dSource.fields)),
                      R.mapObjIndexed((v, f) => {
                        if (f === "updated_at" || f === "created_at") {
                          return typeof v === "string"
                            ? moment(v).format("x") * 1
                            : v;
                        }
                        if (
                          typeof v == "string" &&
                          (v.toLowerCase() == "undefined" ||
                            v.toLowerCase() == "null")
                        ) {
                          return "";
                        }

                        if (
                          dSource.fields[f].type == "array" ||
                          dSource.fields[f].type == "object"
                        ) {
                          return JSON.stringify(v);
                        }

                        if (dSource.fields[f].type == "boolean") {
                          return getBool(v?.toString());
                        }

                        return v || "";
                      })
                    )(requestedModel);

                    datasourceActions.requestEnd();

                    success(model);
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      dSource.delete = (itemId) =>
        new Promise((success, reject) => {
          const { ajaxSession, checkOffline } = api;
          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                datasourceActions.requestStart();

                ajaxSession(`${`${endPointName}`}/${itemId}`, {
                  type: "DELETE",
                })
                  .then(() => {
                    datasourceActions.requestEnd();
                    success();
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      dSource.deleteFromFields = ({ id, fieldName }) =>
        new Promise((success, reject) => {
          const { ajaxSession, checkOffline } = api;
          checkOffline()
            .then((offline) => {
              if (offline) {
                datasourceActions.requestError("offline");
                reject(new Error("offline"));
              } else {
                ajaxSession(`${`${endPointName}`}/${id}/${fieldName}`, {
                  type: "DELETE",
                })
                  .then(() => {
                    success();
                  })
                  .catch((error) => {
                    datasourceActions.requestError(error);
                    reject(error);
                  });
              }
            })
            .catch(reject);
        });

      datasourceActions.created();
      datasourceSuccess(dSource);
    });
